<template>
  <div class="inventory" v-if="!isRoomLoading">
    <div class="inventory__label">
      <input-label :text="currentRoom ?
                          $t('inventory.inventory.labels.addFurniture') :
                          $t('inventory.inventory.labels.chooseRoom')"/>
    </div>
    <div v-if="isMobile" class="inventory__room-selector">
      <el-select class="grey-input" v-model="activeRoom">
        <el-option v-for="(room, index) in rooms"
                   :key="room.id"
                   :value="index"
                   :label="`${room.name} (${getFurnituresCount(room)})`">
        </el-option>
      </el-select>
      <img :class="['inventory__room-selector__burger_menu', !rooms[activeRoom] ? '--hidden' : '']"
           alt="display search bar"
           :src="burgerMenuPath"
           @click="displaySearchBar = !displaySearchBar"
      />
      <font-awesome-icon :icon="['fal', 'trash-alt']"
                         :class="['inventory__header__icon', !rooms[activeRoom] ? '--hidden' : '']"
                         @click="deleteRoom"/>

    </div>
    <div v-else class="inventory__header">
      <inline-selector v-if="!isRoomLoading" :items="rooms"
                       :selectedCategory="activeRoom"
                       @update:item="!furnituresListLoading && onRoomSelected($event)">
        <template v-slot:default="slotPros">
          <div class="inventory__header__room-item" v-if="!(editRoomName && slotPros.isSelected)"
               :class="{'inventory__header__room-item--active':
               !furnituresListLoading && slotPros.isSelected}">
            {{slotPros.item.name}} ({{getFurnituresCount(slotPros.item)}})
          </div>
          <div v-else class="inventory__header__room-item"
               :class="{'inventory__header__room-item--edit': slotPros.isSelected}">
            <el-input :placeholder="slotPros.item.name"
                      class="grey-input"
                      v-model="editingName"
                      v-focus
                      @change="()=>{onEditChange(slotPros)}"
                      @clear="onEditCleared"
                      @blur="onBlur"
            ></el-input>
          </div>
        </template>
      </inline-selector>
      <iconBox @addRoom="!furnituresListLoading && addRoom()"
               @deleteRoom="!furnituresListLoading && deleteRoom()"
               @editRoom="!furnituresListLoading && onEditRoomName()"
               :trashShow="!!currentRoom"
               :editShow="!!currentRoom" />
    </div>
    <transition name="fade" mode="out-in">
      <furniture-selector v-if="currentRoom"
                          :key="currentRoom.id"
                          @update:furniture="updateRoom"
                          :room-furniture="roomFurniture"
                          :auto-disassembly="autoDisassembly"
                          :display-search-bar="displaySearch"
                          :init="init"
                          :loading="furnituresListLoading"
      />
    </transition>
    <disassemble-modal :visible.sync="displayModal"
                       @update:furniture="setFirstFurnitureDisassembly"
                       @update:disassembly="setAutoDisassembly"
    ></disassemble-modal>
    <modal :visible.sync="displayWarningDeletion"
           width="400px"
           :height="'fit-content'"
    >
      <template slot="header">
        <span class="warning-deletion__title"> {{ currentRoomName }} </span>
      </template>
      <span class="warning-deletion__content">
        {{ $t('inventory.warningDeletion', {number: getFurnituresCount(currentRoom)}) }}
      </span>
      <br/>
      <span v-if="isMobile" class="warning-deletion__footer">
        <button class="warning-deletion__footer__left nxt-button nxt-button__grey-light"
                @click="displayWarningDeletion = false">
          {{ $t('base.no') }}
        </button>
        <button @click="deleteRoomWithFurniture" class="nxt-button nxt-button__danger">
           {{ $t('base.delete') }}
        </button>
      </span>
      <template slot="footer" v-else>
        <span class="warning-deletion__footer">
          <button @click="displayWarningDeletion = false"
                  class="warning-deletion__footer__left nxt-button">
            {{ $t('base.no') }}
          </button>
          <button @click="deleteRoomWithFurniture"
                  class="warning-deletion__footer__right nxt-button nxt-button__danger">
            {{ $t('base.delete') }}
          </button>
        </span>
      </template>
    </modal>
  </div>
  <loader v-else></loader>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import InlineSelector from '@/components/utils/InlineSelector.vue';
import FurnitureSelector from './FurnitureSelector.vue';
import DisassembleModal from './modals/DisassembleModal.vue';
import InputLabel from '@/components/utils/InputLabel.vue';
import burger from '@/assets/img/burger_menu.png';
import burgerActif from '@/assets/img/burger_menu_actif.png';
import Modal from '../utils/Modal.vue';
import Loader from '@/components/utils/Loader.vue';
import IconBox from '@/components/furnitures/IconBox.vue';
import { getFurnitureCountInRoom } from '@/store/modules/furnituresStore';

export default {
  name: 'Inventory',
  components: {
    IconBox,
    Loader,
    Modal,
    DisassembleModal,
    FurnitureSelector,
    InlineSelector,
    InputLabel,
  },
  data() {
    return {
      activeRoom: null,
      activeStandard: 0,
      firstDisassembly: false,
      autoDisassembly: undefined,
      displayModal: false,
      firstFurniture: null,
      displaySearchBar: false,
      editRoomName: false,
      previousRoomName: '',
      editingName: '',
      init: false,
      displayWarningDeletion: false,
    };
  },
  computed: {
    ...mapGetters({
      isRoomLoading: 'furnituresStore/roomsLoading',
      furnituresListLoading: 'furnituresStore/furnituresListLoading',
    }),
    rooms() {
      function sortByIdName(rooms) {
        const sortRoomsId = rooms.sort((room1, room2) => room1.id - room2.id);
        const piecesType = [];
        const sortRoomsIdName = [];
        for (const room of sortRoomsId) {
          const findPieceType = piecesType.find(pieceType => pieceType === room.piece_type);
          if (!piecesType.length || !findPieceType) {
            piecesType.push(room.piece_type);
            let filterRoomsPieceType = sortRoomsId.filter(room2 => room2.piece_type
              === room.piece_type);
            if (room.piece_type !== 'custom') {
              filterRoomsPieceType = filterRoomsPieceType.sort((room1, room2) => room1.name
                .localeCompare(room2.name));
            }
            sortRoomsIdName.push(...filterRoomsPieceType);
          }
        }
        return sortRoomsIdName;
      }
      return sortByIdName(this.$store.getters['furnituresStore/rooms']);
    },
    currentRoom() {
      return this.rooms[this.activeRoom];
    },
    currentRoomName() {
      return this.currentRoom ? this.currentRoom.name : '';
    },
    roomFurniture() {
      return this.currentRoom?.meubles || [];
    },
    isMobile() {
      return this.$store.getters['dimensionStore/isMobile'];
    },
    burgerMenuPath() {
      return this.displaySearchBar ? burgerActif : burger;
    },
    displaySearch() {
      return !this.isMobile || this.displaySearchBar;
    },
    standardFurniture() {
      return this.$store.getters['furnituresStore/furnituresList'];
    },
    disassembleEnabled() {
      return this.$store.getters['serviceOfferStore/mount'];
    },
  },
  methods: {
    ...mapActions({
      getFurnituresList: 'furnituresStore/getFurnituresList',
      getRooms: 'furnituresStore/getRooms',
    }),
    onRoomSelected(index) {
      this.activeRoom = index;
    },
    updateRoom({ furniture, quantity, disassembly }) {
      if (furniture && quantity !== undefined) {
        this.updateRoomFurnitureQuantity(furniture, quantity);
      }
      if (furniture && disassembly !== undefined) {
        this.updateRoomFurnitureDisassembly(furniture, disassembly);
      }
    },
    updateRoomFurnitureQuantity(furniture, quantity) {
      const room = this.rooms[this.activeRoom];
      if (room) {
        if (quantity === 0) {
          const index = room.meubles.findIndex(f => f.name === furniture.nom_type
            || f.name === furniture.name);
          room.meubles.splice(index, 1);
        } else {
          this.$store.commit('furnituresStore/SET_ROOM_FURNITURE_QUANTITY',
            { room: this.currentRoom, furniture, quantity });
        }
      }
    },
    updateRoomFurnitureDisassembly(furniture, disassembly) {
      if (!this.firstDisassembly
        && this.disassembleEnabled
        && this.canDisassemble(furniture)) {
        this.firstFurniture = furniture;
        this.firstDisassembly = true;
        this.displayModal = true;
      }
      this.$store.commit('furnituresStore/SET_ROOM_FURNITURE_DISASSEMBLY',
        { room: this.currentRoom, furniture, disassembly });
    },
    setAutoDisassembly(value) {
      this.autoDisassembly = value;
    },
    setFirstFurnitureDisassembly(value) {
      this.updateRoom({ furniture: this.firstFurniture, disassembly: value });
    },
    canDisassemble(furniture) {
      return furniture.demontage_type;
    },
    updateRoomName(id, name) {
      this.$store.commit('furnituresStore/SET_ROOM_NAME', { id, name });
    },
    updateActiveRoom() {
      if (this.rooms[this.activeRoom - 1]) {
        this.onRoomSelected(this.activeRoom - 1);
        return;
      }
      this.onRoomSelected(this.rooms.length > 1 && this.rooms[0] ? 0 : null);
    },
    onEditRoomName() {
      this.editingName = this.currentRoomName;
      this.editRoomName = true;
    },
    onBlur() {
      this.editRoomName = false;
    },
    onEditCleared() {
      this.editingName = this.currentRoomName;
    },
    onEditChange(props) {
      this.editRoomName = false;
      this.updateRoomName(props.item.id, this.editingName);
    },
    deleteRoom() {
      if (this.rooms[this.activeRoom].meubles || this.rooms[this.activeRoom].meubles.length > 0) {
        this.displayWarningDeletion = true;
        return;
      }
      this.deleteRoomWithFurniture();
    },
    addRoom() {
      this.$router.replace({ path: '/furnitures/rooms' });
    },
    deleteRoomWithFurniture() {
      this.$store.dispatch('furnituresStore/deleteRoom', this.rooms[this.activeRoom].id);
      this.displayWarningDeletion = false;
      this.updateActiveRoom();
    },
    getFurnituresCount(room) {
      return getFurnitureCountInRoom(room);
    },
  },
  directives: {
    focus: {
      // directive definition
      inserted(el) {
        el.__vue__.focus();
      },
    },
  },
  watch: {
    activeRoom: {
      handler(value) {
        const room = this.rooms[value];
        if (room) this.editingName = room.name;
      },
      immediate: true,
    },
    editRoom(value) {
      if (value) this.editingName = this.rooms[value].name;
    },
  },
  async created() {
    if (this.standardFurniture.length === 0) {
      this.getFurnituresList()
        // eslint-disable-next-line no-console
        .catch(console.error)
        .finally(() => {
          this.init = true;
        });
    } else {
      this.init = true;
    }
  },
};
</script>
<style scoped lang="scss">
  .fade {
    &-enter-active, &-leave-active {
      transition: opacity 200ms;
    }

    &-enter, &-leave-to {
      opacity: 0;
    }
  }

  .warning-deletion {
    &__title {
      text-align: center;
      display: block;
      font-weight: $--font-weight-500;
      font-size: $--font-size-large;
    }
    &__content{
      display: block;
      text-align: start !important;
      margin: 5px;
      font-size: $--font-size-medium;
    }
    &__furnitures {
      font-weight: $--font-weight-500;
      text-align: start;
      font-size: $--font-size-medium;
    }
    &__footer {
      display: flex;
      justify-content: center;
      margin-top: 20px;
      @include breakpoint(mobile) {
        margin-top: 0;
      }

      &__left {
        margin-right: 15px;
      }
    }
  }

  .el-button{
    margin: 0 15px;
  }

  ::v-deep .el-dialog--center .el-dialog__body {
    text-align: center;
  }

  ::v-deep .dialog__content__body{
    text-align: center;
  }

  .inventory {
    position: relative;
    .--hidden {
      visibility: hidden;
    }

    &__label{
      ::v-deep{
        .input-label {
          margin: 6px 0;
          @include breakpoint(mobileWithHeight){
            margin: 19px 0 0 0;
          }
        }
      }
    }

    &__header {
      display: flex;
      grid-template-columns: auto auto auto;
      place-items: baseline;

      &__room-item {
        padding: 5px 10px;
        margin: 0 7px;
        background-color: $--color-light-grey-blue;
        border-radius: $--border-radius-base;

        &--active {
          background-color: $--color-secondary;
          color: $--color-white;
          border-radius: $--border-radius-base;
        }

        &--edit {
          background: white;
          padding: 0;
        }
      }
    }

    @include breakpoint(tablet) {
      &__header ::v-deep .inline-selector__list {
        max-width: calc(100vw - 60px - 125px);
      }

      .furniture-selector ::v-deep .inline-selector__list {
        max-width: calc(100vw - 60px);
      }
    }

    @include breakpoint(desktop) {
      &__header ::v-deep .inline-selector__list {
        max-width: calc(100vw - 60px - 125px - 600px);
      }

      .furniture-selector ::v-deep .inline-selector__list {
        max-width: calc(100vw - 130px - 600px);
      }
    }

    &__room-selector {
      margin: 10px 0;
      display: grid;
      grid-template-columns: 1fr auto auto;
      align-items: center;
      &__burger_menu {
        margin: 0 .8em;
        cursor: pointer;
      }
    }
  }
</style>
